import { Component, ChangeDetectionStrategy, input, output, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DialogModule } from '@progress/kendo-angular-dialog';

import {
  PolicyDocument,
  Finding,
  ControlDomain,
  DOMAIN_DISPLAY_NAMES,
  FINDING_STATUS_CONFIG,
} from '../../services/policy-gap-analysis.service';
import { ReportCardSummaryComponent } from '../report-card-summary/report-card-summary.component';
import { FindingStatusBadgeComponent } from '../finding-status-badge/finding-status-badge.component';

@Component({
  selector: 'app-document-detail',
  standalone: true,
  imports: [CommonModule, DialogModule, ReportCardSummaryComponent, FindingStatusBadgeComponent],
  templateUrl: './document-detail.component.html',
  styleUrl: './document-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentDetailComponent {
  document = input.required<PolicyDocument>();
  findings = input.required<Finding[]>();

  viewFindings = output<{ documentId: number }>();
  closed = output<void>();

  public documentFindings = computed(() =>
    this.findings().filter((f) => f.document_id === this.document().id)
  );

  public findingsByDomain = computed(() => {
    const grouped = new Map<ControlDomain, Finding[]>();
    for (const finding of this.documentFindings()) {
      const existing = grouped.get(finding.domain) || [];
      existing.push(finding);
      grouped.set(finding.domain, existing);
    }
    return Array.from(grouped.entries()).map(([domain, domainFindings]) => ({
      domain,
      displayName: DOMAIN_DISPLAY_NAMES[domain],
      findings: domainFindings,
      validated: domainFindings.filter((f) => f.status === 'validated').length,
      total: domainFindings.length,
    }));
  });

  public evidenceSources = computed(() => {
    const sources = new Set<string>();
    for (const finding of this.documentFindings()) {
      if (finding.evidence_source) {
        sources.add(finding.evidence_source);
      }
    }
    return Array.from(sources);
  });

  public expandedDomains = new Set<ControlDomain>();

  public toggleDomain(domain: ControlDomain): void {
    if (this.expandedDomains.has(domain)) {
      this.expandedDomains.delete(domain);
    } else {
      this.expandedDomains.add(domain);
    }
  }

  public isDomainExpanded(domain: ControlDomain): boolean {
    return this.expandedDomains.has(domain);
  }

  public getStatusColor(status: string): string {
    return FINDING_STATUS_CONFIG[status as keyof typeof FINDING_STATUS_CONFIG]?.color ?? '#adb5bd';
  }

  public getDomainDisplayName(domain: ControlDomain): string {
    return DOMAIN_DISPLAY_NAMES[domain];
  }

  public formatDate(dateStr: string): string {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }
}
